import React, { useState, useEffect, useMemo } from 'react';
import { PageShell } from '../../components/Ui';
import { useLiveData } from '../../hooks/useLiveData';
import { ipApi } from '../../services/api';
import { useHmsRecords } from '../../hooks/useHmsRecords';
import { escapeHtml } from '../../utils/escapeHtml';

/* ═══════════════════════════════════════════════════════════
   ICU / CRITICAL CARE MODULE
   - Ventilator Parameters
   - APACHE II Scoring
   - Hourly Vitals Flowsheet
   - IV Drip / Infusion Chart
   - Glasgow Coma Scale
   - ICU Nursing Notes
   - Restraint & Sedation Tracking
═══════════════════════════════════════════════════════════ */

const APACHE_QUESTIONS = [
  { id: 'temp', label: 'Temperature (°C)', options: [{ val: 0, label: '36–38.4' }, { val: 1, label: '34–35.9 / 38.5–38.9' }, { val: 2, label: '32–33.9 / 39–40.9' }, { val: 3, label: '<32 or ≥41' }] },
  { id: 'map', label: 'Mean Arterial Pressure (mmHg)', options: [{ val: 0, label: '70–109' }, { val: 2, label: '50–69 / 110–129' }, { val: 3, label: '130–159' }, { val: 4, label: '<50 or ≥160' }] },
  { id: 'hr', label: 'Heart Rate (bpm)', options: [{ val: 0, label: '70–109' }, { val: 2, label: '55–69 / 110–139' }, { val: 3, label: '40–54 / 140–179' }, { val: 4, label: '<40 or ≥180' }] },
  { id: 'rr', label: 'Respiratory Rate (/min)', options: [{ val: 0, label: '12–24' }, { val: 1, label: '10–11 / 25–34' }, { val: 2, label: '6–9 / 35–49' }, { val: 4, label: '<6 or ≥50' }] },
  { id: 'fio2', label: 'Oxygenation (FiO2/PaO2)', options: [{ val: 0, label: 'PaO2 ≥70' }, { val: 1, label: 'PaO2 61–70' }, { val: 2, label: 'PaO2 55–60' }, { val: 4, label: 'PaO2 <55' }] },
  { id: 'ph', label: 'Arterial pH', options: [{ val: 0, label: '7.33–7.49' }, { val: 1, label: '7.25–7.32 / 7.50–7.59' }, { val: 2, label: '7.60–7.69' }, { val: 3, label: '<7.25 or ≥7.70' }] },
  { id: 'na', label: 'Serum Sodium (mEq/L)', options: [{ val: 0, label: '130–149' }, { val: 1, label: '120–129 / 150–154' }, { val: 2, label: '155–159' }, { val: 3, label: '<120 or ≥160' }] },
  { id: 'k', label: 'Serum Potassium (mEq/L)', options: [{ val: 0, label: '3.5–5.4' }, { val: 1, label: '3.0–3.4 / 5.5–5.9' }, { val: 2, label: '2.5–2.9 / 6.0–6.9' }, { val: 4, label: '<2.5 or ≥7.0' }] },
  { id: 'cr', label: 'Creatinine (mg/dL)', options: [{ val: 0, label: '0.6–1.4' }, { val: 2, label: '<0.6 / 1.5–1.9' }, { val: 3, label: '2.0–3.4' }, { val: 4, label: '≥3.5' }] },
  { id: 'hct', label: 'Hematocrit (%)', options: [{ val: 0, label: '30–45.9' }, { val: 1, label: '46–49.9' }, { val: 2, label: '20–29.9 / 50–59.9' }, { val: 4, label: '<20 or ≥60' }] },
  { id: 'wbc', label: 'WBC (×1000/mm³)', options: [{ val: 0, label: '3–14.9' }, { val: 1, label: '15–19.9' }, { val: 2, label: '1–2.9 / 20–39.9' }, { val: 4, label: '<1 or ≥40' }] },
];

const GCS_EYES = [{ val: 4, label: 'Spontaneous (4)' }, { val: 3, label: 'To Voice (3)' }, { val: 2, label: 'To Pain (2)' }, { val: 1, label: 'None (1)' }];
const GCS_VERBAL = [{ val: 5, label: 'Oriented (5)' }, { val: 4, label: 'Confused (4)' }, { val: 3, label: 'Inappropriate Words (3)' }, { val: 2, label: 'Sounds (2)' }, { val: 1, label: 'None (1)' }];
const GCS_MOTOR = [{ val: 6, label: 'Obeys (6)' }, { val: 5, label: 'Localises (5)' }, { val: 4, label: 'Withdraws (4)' }, { val: 3, label: 'Flexion (3)' }, { val: 2, label: 'Extension (2)' }, { val: 1, label: 'None (1)' }];

function mapIcuPatient(a) {
  return {
    id: a.admissionId || a.id,
    name: a.patientName || a.name,
    age: a.age || '—',
    diag: a.diagnosis || a.reason || a.diag || 'Critical care',
    bed: a.bedLabel || a.bedId || a.bed || a.ward,
    doctor: a.doctor || '—',
    since: a.admittedAt ? new Date(a.admittedAt).toLocaleDateString('en-IN') : (a.since || '—'),
    ventilated: Boolean(a.ventilated),
    status: a.icuStatus || a.status || 'Serious',
  };
}

function isIcuWard(wardOrBed) {
  const w = String(wardOrBed || '').toUpperCase();
  return w.includes('ICU') || w.includes('CCU');
}

function VentilatorPanel({ patient }) {
  const { records, create, loading } = useHmsRecords('icu-vent', 200);
  const patientRecords = useMemo(() => (
    (records || [])
      .filter((r) => !patient?.id || r.patientId === patient.id)
      .sort((a, b) => new Date(b.at || b.createdAt) - new Date(a.at || a.createdAt))
  ), [records, patient]);
  const latest = patientRecords[0];
  const [params, setParams] = useState({ mode: 'A/C-VC', tv: 500, rr: 14, peep: 5, fio2: 40, pip: 22, pplat: 18, ie: '1:2', trigger: -2 });
  const [abg, setAbg] = useState({ pH: 7.38, pco2: 42, po2: 85, hco3: 24, sao2: 97, fio2: 40 });
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!latest) return;
    if (latest.params) setParams(latest.params);
    if (latest.abg) setAbg(latest.abg);
  }, [latest?.recordId]);

  const pf = Math.round((Number(abg.po2) / (Number(abg.fio2) / 100)) || 0);

  const printVentSheet = () => {
    const win = window.open('', '_blank', 'width=800,height=900');
    if (!win) return;
    const when = new Date().toLocaleString('en-IN');
    const e = escapeHtml;
    win.document.write(`<!DOCTYPE html><html><head><title>Ventilator Sheet</title>
      <style>
        body{font-family:Arial,sans-serif;max-width:720px;margin:24px auto;color:#0f172a;font-size:13px}
        h1{font-size:18px;margin:0 0 4px} .sub{color:#64748b;margin-bottom:16px}
        table{width:100%;border-collapse:collapse;margin:12px 0}
        th,td{border:1px solid #e2e8f0;padding:8px 10px;text-align:left}
        th{background:#f8fafc;width:40%}
        .banner{padding:10px 12px;border-radius:8px;background:${pf < 200 ? '#fef2f2' : '#f0fdf4'};margin:12px 0}
      </style></head><body>
      <h1>ICU Ventilator Flow Sheet</h1>
      <div class="sub">${e(when)}</div>
      <table>
        <tr><th>Patient</th><td>${e(patient?.name || '—')} (${e(patient?.id || '—')})</td></tr>
        <tr><th>Mode</th><td>${e(params.mode)}</td></tr>
        <tr><th>Tidal Volume</th><td>${e(params.tv)} mL</td></tr>
        <tr><th>RR</th><td>${e(params.rr)} /min</td></tr>
        <tr><th>PEEP</th><td>${e(params.peep)} cmH₂O</td></tr>
        <tr><th>FiO₂</th><td>${e(params.fio2)}%</td></tr>
        <tr><th>I:E</th><td>${e(params.ie)}</td></tr>
        <tr><th>PIP / Pplat</th><td>${e(params.pip || '—')} / ${e(params.pplat || '—')} cmH₂O</td></tr>
      </table>
      <h2 style="font-size:15px">ABG</h2>
      <table>
        <tr><th>pH</th><td>${e(abg.pH)}</td></tr>
        <tr><th>PaCO₂</th><td>${e(abg.pco2)} mmHg</td></tr>
        <tr><th>PaO₂</th><td>${e(abg.po2)} mmHg</td></tr>
        <tr><th>HCO₃</th><td>${e(abg.hco3)} mEq/L</td></tr>
        <tr><th>SaO₂</th><td>${e(abg.sao2)}%</td></tr>
      </table>
      <div class="banner"><strong>P/F Ratio:</strong> ${e(pf)}
        (${pf < 100 ? 'Severe ARDS' : pf < 200 ? 'Moderate ARDS' : pf < 300 ? 'Mild ARDS' : 'Normal'})
      </div>
      <p style="color:#64748b;font-size:11px">Printed from HCEN ICU · verify against bedside ventilator before clinical use.</p>
      <script>window.print()</script>
    </body></html>`);
    win.document.close();
  };

  const applyChanges = async () => {
    if (!patient?.id) return;
    setBusy(true);
    setSaved(false);
    try {
      await create({
        patientId: patient.id,
        patientName: patient.name,
        params,
        abg,
        pfRatio: pf,
        at: new Date().toISOString(),
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      window.alert(e.message || 'Could not save ventilator settings');
    } finally {
      setBusy(false);
    }
  };

  if (!patient) return <p className="hint">Select an ICU patient to manage ventilator settings.</p>;

  return (
    <div>
      {loading && <p className="hint mb">Loading ventilator history…</p>}
      {saved && <div className="info-banner mb">✓ Ventilator settings saved</div>}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))', gap: 10, marginBottom: 16 }}>
        {[
          { label: 'Mode', val: params.mode, color: '#6366f1' },
          { label: 'Tidal Volume', val: `${params.tv} mL`, color: '#3b82f6' },
          { label: 'Set RR', val: `${params.rr} /min`, color: '#10b981' },
          { label: 'PEEP', val: `${params.peep} cmH₂O`, color: '#f59e0b' },
          { label: 'FiO₂', val: `${params.fio2}%`, color: '#06b6d4' },
          { label: 'P/F Ratio', val: pf, color: pf < 100 ? '#dc2626' : pf < 200 ? '#f97316' : '#10b981' },
        ].map(s => (
          <div key={s.label} className="card" style={{ padding: 12, textAlign: 'center', borderTop: `3px solid ${s.color}` }}>
            <div style={{ fontFamily: 'Outfit,sans-serif', fontSize: 20, fontWeight: 900, color: s.color }}>{s.val}</div>
            <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', marginTop: 2 }}>{s.label}</div>
          </div>
        ))}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div className="card">
          <h4 style={{ marginTop: 0, color: '#6366f1' }}>⚙️ Ventilator Settings</h4>
          <div className="form-grid">
            {[['Mode', 'mode', ['A/C-VC', 'A/C-PC', 'SIMV', 'PSV', 'CPAP', 'PRVC']], ['TV (mL)', 'tv', null], ['RR (/min)', 'rr', null], ['PEEP (cmH₂O)', 'peep', null], ['FiO₂ (%)', 'fio2', null], ['I:E Ratio', 'ie', ['1:1', '1:1.5', '1:2', '1:2.5', '1:3']]].map(([label, key, opts]) => (
              <div key={key} className="field">
                <label>{label}</label>
                {opts ? (
                  <select className="input" value={params[key]} onChange={e => setParams(p => ({ ...p, [key]: e.target.value }))}>
                    {opts.map(o => <option key={o}>{o}</option>)}
                  </select>
                ) : (
                  <input type="number" className="input" value={params[key]} onChange={e => setParams(p => ({ ...p, [key]: e.target.value }))} />
                )}
              </div>
            ))}
          </div>
          <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
            <button type="button" className="btn btn-primary btn-sm" disabled={busy} onClick={applyChanges}>{busy ? 'Saving…' : '✓ Apply Changes'}</button>
            <button type="button" className="btn btn-secondary btn-sm" onClick={printVentSheet}>🖨 Print Vent Sheet</button>
          </div>
        </div>
        <div className="card">
          <h4 style={{ marginTop: 0, color: '#06b6d4' }}>🧪 ABG / Oxygenation</h4>
          <div className="form-grid">
            {[['pH', 'pH'], ['PaCO₂ (mmHg)', 'pco2'], ['PaO₂ (mmHg)', 'po2'], ['HCO₃ (mEq/L)', 'hco3'], ['SaO₂ (%)', 'sao2'], ['FiO₂ (%)', 'fio2']].map(([label, key]) => (
              <div key={key} className="field">
                <label>{label}</label>
                <input type="number" className="input" step="0.01" value={abg[key]} onChange={e => setAbg(a => ({ ...a, [key]: e.target.value }))} />
              </div>
            ))}
          </div>
          <div style={{ marginTop: 10, padding: '10px 14px', background: pf < 200 ? '#fef2f2' : '#f0fdf4', borderRadius: 8, display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ fontWeight: 700 }}>P/F Ratio: <strong style={{ color: pf < 100 ? '#dc2626' : pf < 200 ? '#f97316' : '#10b981', fontSize: 18 }}>{pf}</strong></span>
            <span style={{ fontSize: 12, color: '#64748b' }}>{pf < 100 ? 'Severe ARDS' : pf < 200 ? 'Moderate ARDS' : pf < 300 ? 'Mild ARDS' : 'Normal'}</span>
          </div>
        </div>
      </div>
      {patientRecords.length > 0 && (
        <div className="card mt" style={{ marginTop: 14 }}>
          <h4 style={{ marginTop: 0 }}>Recent ventilator snapshots</h4>
          {patientRecords.slice(0, 5).map((r) => (
            <div key={r.recordId || r.id} style={{ fontSize: 12, padding: '6px 0', borderBottom: '1px solid #f1f5f9' }}>
              {new Date(r.at || r.createdAt).toLocaleString('en-IN')} — {r.params?.mode || '—'} · TV {r.params?.tv} · FiO₂ {r.params?.fio2}% · P/F {r.pfRatio ?? '—'}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ApacheTab({ patient }) {
  const { records, create, loading } = useHmsRecords('icu-apache', 100);
  const savedScores = useMemo(() => (
    (records || [])
      .filter((r) => !patient?.id || r.patientId === patient.id)
      .sort((a, b) => new Date(b.at || b.createdAt) - new Date(a.at || a.createdAt))
  ), [records, patient]);
  const [scores, setScores] = useState({});
  const [age, setAge] = useState(patient?.age || 50);
  const [chronic, setChronic] = useState(0);
  const [gcs, setGcs] = useState({ eyes: 4, verbal: 5, motor: 6 });
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (patient?.age) setAge(Number(patient.age) || 50);
  }, [patient?.id, patient?.age]);

  const apsScore = Object.values(scores).reduce((s, v) => s + Number(v || 0), 0);
  const ageScore = age < 45 ? 0 : age < 55 ? 2 : age < 65 ? 3 : age < 75 ? 5 : 6;
  const gcsScore = 15 - (gcs.eyes + gcs.verbal + gcs.motor);
  const total = apsScore + ageScore + Number(chronic) + gcsScore;
  const mortality = total <= 4 ? '4%' : total <= 9 ? '8%' : total <= 14 ? '15%' : total <= 19 ? '25%' : total <= 24 ? '40%' : total <= 29 ? '55%' : '73%+';
  const riskColor = total <= 9 ? '#10b981' : total <= 19 ? '#f59e0b' : '#dc2626';

  const saveScore = async () => {
    if (!patient?.id) return;
    setBusy(true);
    setSaved(false);
    try {
      await create({
        patientId: patient.id,
        patientName: patient.name,
        scores,
        age,
        chronic: Number(chronic),
        gcs,
        apsScore,
        ageScore,
        gcsScore,
        total,
        mortality,
        at: new Date().toISOString(),
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } finally {
      setBusy(false);
    }
  };

  if (!patient) return <p className="hint">Select an ICU patient to calculate APACHE II.</p>;

  return (
    <div>
      {loading && <p className="hint mb">Loading saved scores…</p>}
      {saved && <div className="info-banner mb">✓ APACHE II score saved</div>}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12 }}>
        <div>
          <div className="card mb">
            <h4 style={{ marginTop: 0 }}>APS — Acute Physiology Score (12 variables)</h4>
            {APACHE_QUESTIONS.map(q => (
              <div key={q.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #f1f5f9', gap: 12 }}>
                <span style={{ fontSize: 13, fontWeight: 600, flex: 1 }}>{q.label}</span>
                <select className="input" style={{ width: 'auto', fontSize: 12 }} value={scores[q.id] || ''} onChange={e => setScores(s => ({ ...s, [q.id]: e.target.value }))}>
                  <option value="">Select</option>
                  {q.options.map(o => <option key={o.val} value={o.val}>{o.label} (+{o.val})</option>)}
                </select>
              </div>
            ))}
          </div>
          <div className="card mb">
            <h4 style={{ marginTop: 0 }}>Glasgow Coma Scale</h4>
            <div className="form-grid">
              <div className="field"><label>Eye Opening</label><select className="input" value={gcs.eyes} onChange={e => setGcs(g => ({ ...g, eyes: Number(e.target.value) }))}>{GCS_EYES.map(o => <option key={o.val} value={o.val}>{o.label}</option>)}</select></div>
              <div className="field"><label>Verbal Response</label><select className="input" value={gcs.verbal} onChange={e => setGcs(g => ({ ...g, verbal: Number(e.target.value) }))}>{GCS_VERBAL.map(o => <option key={o.val} value={o.val}>{o.label}</option>)}</select></div>
              <div className="field"><label>Motor Response</label><select className="input" value={gcs.motor} onChange={e => setGcs(g => ({ ...g, motor: Number(e.target.value) }))}>{GCS_MOTOR.map(o => <option key={o.val} value={o.val}>{o.label}</option>)}</select></div>
            </div>
            <div style={{ marginTop: 8, padding: '8px 12px', background: '#f8fafc', borderRadius: 8 }}>
              <span style={{ fontWeight: 700 }}>GCS Total: </span>
              <span style={{ fontFamily: 'Outfit,sans-serif', fontSize: 20, fontWeight: 900, color: (gcs.eyes + gcs.verbal + gcs.motor) <= 8 ? '#dc2626' : '#10b981' }}>{gcs.eyes + gcs.verbal + gcs.motor}/15</span>
              <span style={{ fontSize: 12, color: '#94a3b8', marginLeft: 8 }}>{(gcs.eyes + gcs.verbal + gcs.motor) <= 8 ? 'Coma' : (gcs.eyes + gcs.verbal + gcs.motor) <= 12 ? 'Moderate' : 'Mild/Normal'}</span>
            </div>
          </div>
        </div>
        <div>
          <div className="card mb" style={{ borderTop: `4px solid ${riskColor}` }}>
            <h4 style={{ marginTop: 0, color: riskColor }}>APACHE II Score</h4>
            {[['APS Score', apsScore, '#6366f1'], ['Age Score', ageScore, '#3b82f6'], ['GCS Contribution', gcsScore, '#f59e0b'], ['Chronic Health', chronic, '#94a3b8']].map(([label, val, color]) => (
              <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #f1f5f9' }}>
                <span style={{ fontSize: 13, color: '#475569' }}>{label}</span>
                <span style={{ fontWeight: 700, color }}>{val}</span>
              </div>
            ))}
            <div style={{ padding: '12px 0 0', display: 'flex', justifyContent: 'space-between', fontWeight: 800 }}>
              <span>TOTAL</span>
              <span style={{ fontFamily: 'Outfit,sans-serif', fontSize: 28, color: riskColor }}>{total}</span>
            </div>
            <div style={{ marginTop: 10, padding: '10px 12px', background: riskColor + '15', borderRadius: 8, textAlign: 'center' }}>
              <div style={{ fontWeight: 800, color: riskColor, fontSize: 18 }}>Est. Mortality: {mortality}</div>
              <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>Score ≥ 25 = Severe risk</div>
            </div>
          </div>
          <div className="card">
            <h4 style={{ marginTop: 0 }}>Age Points</h4>
            <select className="input mb" value={age} onChange={e => setAge(Number(e.target.value))}>
              {[30, 35, 40, 45, 50, 55, 60, 65, 70, 75, 80].map(a => <option key={a} value={a}>{a} yrs — +{a < 45 ? 0 : a < 55 ? 2 : a < 65 ? 3 : a < 75 ? 5 : 6} pts</option>)}
            </select>
            <h4 style={{ marginTop: 8 }}>Chronic Health</h4>
            <select className="input" value={chronic} onChange={e => setChronic(e.target.value)}>
              <option value={0}>None (0)</option>
              <option value={2}>Nonoperative / Elective post-op (+2)</option>
              <option value={5}>Emergency post-op (+5)</option>
            </select>
            <button type="button" className="btn btn-primary btn-sm mt" style={{ marginTop: 10, width: '100%' }} disabled={busy} onClick={saveScore}>
              {busy ? 'Saving…' : '✓ Save APACHE Score'}
            </button>
          </div>
        </div>
      </div>
      {savedScores.length > 0 && (
        <div className="card mt" style={{ marginTop: 14 }}>
          <h4 style={{ marginTop: 0 }}>Saved APACHE II scores</h4>
          <table className="data-table">
            <thead><tr><th>Date</th><th>Total</th><th>APS</th><th>Est. mortality</th></tr></thead>
            <tbody>
              {savedScores.slice(0, 10).map((r) => (
                <tr key={r.recordId || r.id}>
                  <td>{new Date(r.at || r.createdAt).toLocaleString('en-IN')}</td>
                  <td><strong style={{ color: (r.total || 0) > 19 ? '#dc2626' : '#0d6e6a' }}>{r.total}</strong></td>
                  <td>{r.apsScore}</td>
                  <td>{r.mortality}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function HourlyVitalsTab({ patient }) {
  const { records, create, loading } = useHmsRecords('icu-vitals', 200);
  const vitals = useMemo(() => (
    (records || [])
      .filter((r) => !patient?.id || r.patientId === patient.id)
      .sort((a, b) => new Date(b.at || b.createdAt) - new Date(a.at || a.createdAt))
  ), [records, patient]);
  const [addRow, setAddRow] = useState({ time: '', sbp: '', dbp: '', hr: '', spo2: '', temp: '', rr: '', cvp: '', uo: '' });
  const [busy, setBusy] = useState(false);

  const submitRow = async () => {
    if (!patient?.id || !addRow.time) return;
    setBusy(true);
    try {
      await create({
        patientId: patient.id,
        patientName: patient.name,
        ...addRow,
        at: new Date().toISOString(),
      });
      setAddRow({ time: '', sbp: '', dbp: '', hr: '', spo2: '', temp: '', rr: '', cvp: '', uo: '' });
    } finally {
      setBusy(false);
    }
  };

  if (!patient) return <p className="hint">Select an ICU patient to chart vitals.</p>;

  return (
    <div>
      {loading && <p className="hint mb">Loading vitals…</p>}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ borderCollapse: 'collapse', fontSize: 12, width: '100%', minWidth: 900 }}>
          <thead>
            <tr style={{ background: '#1e293b', color: '#fff' }}>
              {['Time', 'BP (mmHg)', 'HR', 'SpO₂%', 'Temp°C', 'RR', 'CVP', 'UO (mL/hr)'].map(h => (
                <th key={h} style={{ padding: '8px 10px', textAlign: 'center', fontWeight: 700, whiteSpace: 'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {vitals.map((v, i) => {
              const spoCrit = Number(v.spo2) < 92;
              const hrCrit = Number(v.hr) > 120 || Number(v.hr) < 50;
              return (
                <tr key={v.recordId || v.id || i} style={{ background: (spoCrit || hrCrit) ? '#fef2f2' : i % 2 === 0 ? '#f8fafc' : '#fff' }}>
                  <td style={{ padding: '7px 10px', fontWeight: 700, textAlign: 'center' }}>{v.time}</td>
                  <td style={{ padding: '7px 10px', textAlign: 'center', fontWeight: 600 }}>{v.sbp}/{v.dbp}</td>
                  <td style={{ padding: '7px 10px', textAlign: 'center', color: hrCrit ? '#dc2626' : 'inherit', fontWeight: hrCrit ? 800 : 400 }}>{hrCrit && '⚠ '}{v.hr}</td>
                  <td style={{ padding: '7px 10px', textAlign: 'center', color: spoCrit ? '#dc2626' : '#10b981', fontWeight: 700 }}>{spoCrit && '⚠ '}{v.spo2}%</td>
                  <td style={{ padding: '7px 10px', textAlign: 'center' }}>{v.temp}</td>
                  <td style={{ padding: '7px 10px', textAlign: 'center' }}>{v.rr}</td>
                  <td style={{ padding: '7px 10px', textAlign: 'center' }}>{v.cvp}</td>
                  <td style={{ padding: '7px 10px', textAlign: 'center', color: Number(v.uo) < 30 ? '#dc2626' : '#10b981', fontWeight: 700 }}>{Number(v.uo) < 30 && '⚠ '}{v.uo}</td>
                </tr>
              );
            })}
            {vitals.length === 0 && (
              <tr><td colSpan={8} style={{ padding: 16, textAlign: 'center', color: '#94a3b8' }}>No vitals logged yet — add first entry below.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="card mt" style={{ marginTop: 14 }}>
        <h4 style={{ marginTop: 0 }}>+ Add vitals entry</h4>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          {[['Time', 'time', 'time'], ['SBP', 'sbp', 'number'], ['DBP', 'dbp', 'number'], ['HR', 'hr', 'number'], ['SpO₂%', 'spo2', 'number'], ['Temp', 'temp', 'number'], ['RR', 'rr', 'number'], ['CVP', 'cvp', 'number'], ['UO ml/hr', 'uo', 'number']].map(([label, key, type]) => (
            <div key={key} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <label style={{ fontSize: 11, fontWeight: 700, color: '#475569' }}>{label}</label>
              <input type={type} className="input" style={{ width: 80 }} value={addRow[key]} onChange={e => setAddRow(r => ({ ...r, [key]: e.target.value }))} />
            </div>
          ))}
          <button type="button" className="btn btn-primary btn-sm" style={{ alignSelf: 'flex-end' }} disabled={busy || !addRow.time} onClick={submitRow}>
            {busy ? 'Saving…' : '+ Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

function DripChartTab({ patient }) {
  const { records: allDrips, create, patch, loading } = useHmsRecords('icu-drip', 200);
  const drips = useMemo(() => (
    (allDrips || []).filter((d) => !patient?.id || d.patientId === patient.id)
  ), [allDrips, patient]);
  const [form, setForm] = useState({ drug: '', conc: '', rate: '', unit: 'mL/hr', route: 'CVC', by: '' });
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);

  const addDrip = async () => {
    if (!patient?.id || !form.drug.trim()) return;
    setBusy(true);
    try {
      await create({
        patientId: patient.id,
        patientName: patient.name,
        ...form,
        rate: Number(form.rate) || form.rate,
        started: new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }),
        status: 'Running',
      });
      setForm({ drug: '', conc: '', rate: '', unit: 'mL/hr', route: 'CVC', by: '' });
      setShowForm(false);
    } finally {
      setBusy(false);
    }
  };

  if (!patient) return <p className="hint">Select an ICU patient to manage infusions.</p>;

  return (
    <div>
      <div style={{ marginBottom: 12 }}>
        <button type="button" className="btn btn-primary" onClick={() => setShowForm(s => !s)}>{showForm ? '✕ Cancel' : '+ Add Infusion'}</button>
      </div>
      {showForm && (
        <div className="card mb form-grid" style={{ borderLeft: '4px solid #8b5cf6' }}>
          <div className="field"><label>Drug Name</label><input className="input" value={form.drug} onChange={e => setForm(f => ({ ...f, drug: e.target.value }))} placeholder="e.g. Dopamine" /></div>
          <div className="field"><label>Concentration</label><input className="input" value={form.conc} onChange={e => setForm(f => ({ ...f, conc: e.target.value }))} placeholder="e.g. 200mg/50mL" /></div>
          <div className="field"><label>Rate</label><input type="number" className="input" value={form.rate} onChange={e => setForm(f => ({ ...f, rate: e.target.value }))} /></div>
          <div className="field"><label>Unit</label><select className="input" value={form.unit} onChange={e => setForm(f => ({ ...f, unit: e.target.value }))}><option>mL/hr</option><option>mcg/kg/min</option><option>IU/hr</option><option>mg/hr</option><option>Units/hr</option></select></div>
          <div className="field"><label>Route</label><select className="input" value={form.route} onChange={e => setForm(f => ({ ...f, route: e.target.value }))}><option>CVC</option><option>PIV</option><option>Arterial</option><option>Epidural</option></select></div>
          <div className="field"><label>Ordered By</label><input className="input" value={form.by} onChange={e => setForm(f => ({ ...f, by: e.target.value }))} /></div>
          <div className="full form-actions"><button type="button" className="btn btn-primary" disabled={busy} onClick={addDrip}>{busy ? 'Saving…' : '+ Add Infusion'}</button></div>
        </div>
      )}
      {loading && <p className="hint mb">Loading infusions…</p>}
      <div className="card">
        <h3 style={{ marginTop: 0 }}>🩺 Active Infusions / Drips</h3>
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr><th>Drug</th><th>Concentration</th><th>Rate</th><th>Route</th><th>Started</th><th>By</th><th>Status</th><th>Action</th></tr>
            </thead>
            <tbody>
              {drips.map(d => (
                <tr key={d.recordId || d.id}>
                  <td style={{ fontWeight: 700 }}>{d.drug}</td>
                  <td style={{ fontSize: 12, color: '#64748b' }}>{d.conc}</td>
                  <td><strong style={{ color: '#8b5cf6' }}>{d.rate}</strong> <span style={{ fontSize: 11, color: '#94a3b8' }}>{d.unit}</span></td>
                  <td><span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: d.route === 'CVC' ? '#eff6ff' : '#f0fdf4', color: d.route === 'CVC' ? '#2563eb' : '#16a34a' }}>{d.route}</span></td>
                  <td>{d.started}</td>
                  <td style={{ fontSize: 12 }}>{d.by}</td>
                  <td><span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: d.status === 'Running' ? '#f0fdf4' : '#fff7ed', color: d.status === 'Running' ? '#16a34a' : '#c2410c' }}>{d.status}</span></td>
                  <td>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button type="button" style={{ fontSize: 11, padding: '2px 8px', background: '#fff7ed', border: '1px solid #fed7aa', color: '#c2410c', borderRadius: 5, cursor: 'pointer', fontWeight: 600 }} onClick={() => patch(d.recordId || d.id, { status: 'On Hold' })}>Hold</button>
                      <button type="button" style={{ fontSize: 11, padding: '2px 8px', background: '#fef2f2', border: '1px solid #fca5a5', color: '#dc2626', borderRadius: 5, cursor: 'pointer', fontWeight: 600 }} onClick={() => patch(d.recordId || d.id, { status: 'Stopped' })}>Stop</button>
                    </div>
                  </td>
                </tr>
              ))}
              {drips.length === 0 && (
                <tr><td colSpan={8} style={{ padding: 16, textAlign: 'center', color: '#94a3b8' }}>No active infusions — add one above.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function IcuNotesTab({ patient, notes, onSaveNote }) {
  const patientNotes = (notes || []).filter((n) => !n.patientId || n.patientId === patient?.id);
  const [form, setForm] = useState({ type: 'Doctor', author: '', note: '' });
  const [busy, setBusy] = useState(false);

  return (
    <div>
      <div className="card mb" style={{ borderLeft: '4px solid #0d6e6a' }}>
        <h4 style={{ marginTop: 0 }}>Add ICU Note</h4>
        <div className="form-grid mb">
          <div className="field"><label>Type</label><select className="input" value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))}><option>Doctor</option><option>Nursing</option><option>Physiotherapy</option><option>Dietitian</option></select></div>
          <div className="field"><label>Author</label><input className="input" value={form.author} onChange={e => setForm(f => ({ ...f, author: e.target.value }))} placeholder="Your name" /></div>
          <div className="field full"><label>Note *</label><textarea className="input" rows={4} value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))} placeholder="Clinical note, observations, plan..." /></div>
        </div>
        <div className="form-actions">
          <button type="button" className="btn btn-primary" disabled={busy} onClick={async () => {
            if (!form.note.trim() || !onSaveNote || !patient) return;
            setBusy(true);
            try {
              await onSaveNote({ patientId: patient.id, patientName: patient.name, ...form, at: new Date().toISOString() });
              setForm((f) => ({ ...f, note: '' }));
            } finally { setBusy(false); }
          }}>{busy ? 'Saving…' : 'Save Note'}</button>
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {patientNotes.map(n => (
          <div key={n.id || n.recordId || n.at} className="card" style={{ borderLeft: `4px solid ${n.type === 'Doctor' ? '#6366f1' : n.type === 'Nursing' ? '#10b981' : '#f59e0b'}` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
              <div><span style={{ fontWeight: 800 }}>{n.author}</span><span style={{ fontSize: 11, padding: '1px 7px', borderRadius: 999, background: '#f1f5f9', color: '#475569', marginLeft: 8 }}>{n.type}</span></div>
              <span style={{ fontSize: 12, color: '#94a3b8' }}>{new Date(n.at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
            </div>
            <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.6, color: '#1e293b' }}>{n.note}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function RestraintTab({ patient }) {
  const { records, create, patch, loading } = useHmsRecords('icu-restraint', 100);
  const rows = (records || []).filter((r) => !patient?.id || r.patientId === patient.id);
  const [form, setForm] = useState({ type: 'Physical restraint', reason: '', sedative: '', dose: '', by: '' });
  const [busy, setBusy] = useState(false);

  return (
    <div>
      <div className="info-banner mb">Document every restraint / sedation episode for NABH. Reassess within 4 hours.</div>
      <div className="card mb form-grid" style={{ borderLeft: '4px solid #f97316' }}>
        <div className="field"><label>Type</label>
          <select className="input" value={form.type} onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}>
            {['Physical restraint', 'Chemical sedation', 'Both'].map((t) => <option key={t}>{t}</option>)}
          </select>
        </div>
        <div className="field"><label>Reason *</label><input className="input" value={form.reason} onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))} placeholder="Agitation / pull lines / safety" /></div>
        <div className="field"><label>Sedative</label><input className="input" value={form.sedative} onChange={(e) => setForm((f) => ({ ...f, sedative: e.target.value }))} placeholder="e.g. Midazolam" /></div>
        <div className="field"><label>Dose</label><input className="input" value={form.dose} onChange={(e) => setForm((f) => ({ ...f, dose: e.target.value }))} /></div>
        <div className="field"><label>Ordered by</label><input className="input" value={form.by} onChange={(e) => setForm((f) => ({ ...f, by: e.target.value }))} /></div>
        <div className="full form-actions">
          <button type="button" className="btn btn-primary" disabled={busy || !patient} onClick={async () => {
            if (!form.reason.trim()) return;
            setBusy(true);
            try {
              await create({
                patientId: patient.id,
                patientName: patient.name,
                uhid: patient.uhid || '',
                ...form,
                status: 'Active',
                startedAt: new Date().toISOString(),
              });
              setForm({ type: 'Physical restraint', reason: '', sedative: '', dose: '', by: '' });
            } finally { setBusy(false); }
          }}>{busy ? 'Saving…' : 'Start episode'}</button>
        </div>
      </div>
      {loading && <p className="hint">Loading…</p>}
      {rows.map((r) => (
        <div key={r.recordId || r.id} className="card mb" style={{ borderLeft: '4px solid #f97316' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
            <strong>{r.type}</strong>
            <span style={{ fontSize: 11 }}>{r.status}</span>
          </div>
          <div style={{ fontSize: 13, marginTop: 6 }}>{r.reason}</div>
          <div style={{ fontSize: 12, color: '#64748b' }}>{r.sedative} {r.dose} · {r.by} · {r.startedAt ? new Date(r.startedAt).toLocaleString('en-IN') : ''}</div>
          {r.status === 'Active' && (
            <button type="button" className="btn btn-sm btn-secondary" style={{ marginTop: 8 }} onClick={() => patch(r.recordId || r.id, { status: 'Released', endedAt: new Date().toISOString() })}>Release</button>
          )}
        </div>
      ))}
    </div>
  );
}

export default function IcuPage() {
  const { data: admData } = useLiveData(() => ipApi.getAdmissions(80), []);
  const { data: bedData } = useLiveData(() => ipApi.getBeds(120), []);
  const { records: censusRecords } = useHmsRecords('icu-census');
  const { records: icuNotes, create: createNote } = useHmsRecords('icu-note');
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [tab, setTab] = useState('vitals');

  const patients = useMemo(() => {
    const fromAdm = (admData?.admissions || [])
      .filter((a) => isIcuWard(a.ward || a.bedLabel || a.bedId) && a.status !== 'discharged')
      .map(mapIcuPatient);
    if (fromAdm.length) return fromAdm;
    if (censusRecords.length) return censusRecords.map(mapIcuPatient);
    return [];
  }, [admData, censusRecords]);

  const icuBedStats = useMemo(() => {
    const beds = (bedData?.beds || []).filter((b) => isIcuWard(b.ward || b.bedLabel || b.bedId || b.room));
    if (!beds.length) {
      const capacity = Math.max(patients.length, Number(bedData?.icuCapacity) || 0);
      return { available: capacity ? Math.max(0, capacity - patients.length) : '—' };
    }
    const available = beds.filter((b) => String(b.status || '').toLowerCase() === 'vacant').length;
    return { available };
  }, [bedData, patients.length]);

  useEffect(() => {
    if (patients.length && !selectedPatient) setSelectedPatient(patients[0]);
  }, [patients, selectedPatient]);

  const statusColor = { Critical: '#dc2626', Serious: '#f97316', Stable: '#f59e0b', Improving: '#10b981' };

  return (
    <PageShell
      title="ICU / Critical Care"
      subtitle="Ventilator · APACHE II · Hourly Vitals · Drip Chart · ICU Notes"
      actions={<button type="button" className="btn btn-secondary btn-sm" onClick={() => window.print()}>🖨 Print ICU Sheet</button>}
    >
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: 8, marginBottom: 16 }}>
        {[{ label: 'ICU Census', val: patients.length, color: '#6366f1' }, { label: 'Ventilated', val: patients.filter(p => p.ventilated).length, color: '#3b82f6' }, { label: 'Critical', val: patients.filter(p => p.status === 'Critical').length, color: '#dc2626' }, { label: 'Available Beds', val: icuBedStats.available, color: '#10b981' }].map(s => (
          <div key={s.label} className="card" style={{ padding: 12, textAlign: 'center', borderTop: `3px solid ${s.color}` }}>
            <div style={{ fontFamily: 'Outfit,sans-serif', fontSize: 22, fontWeight: 900, color: s.color }}>{s.val}</div>
            <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', marginTop: 2 }}>{s.label}</div>
          </div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '220px 1fr', gap: 14 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ fontSize: 12, fontWeight: 800, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em', paddingBottom: 4 }}>ICU Patients</div>
          {patients.length === 0 && (
            <p className="hint table-empty">No ICU admissions found. Admit a patient to ICU or assign an ICU ward bed from IPD.</p>
          )}
          {patients.map(p => (
            <div
              key={p.id}
              onClick={() => setSelectedPatient(p)}
              style={{ padding: '12px 14px', borderRadius: 10, cursor: 'pointer', border: `2px solid ${selectedPatient?.id === p.id ? '#0d6e6a' : '#e2e8f0'}`, background: selectedPatient?.id === p.id ? '#f0fdfa' : '#fff', transition: 'all 0.2s' }}
            >
              <div style={{ fontWeight: 700, fontSize: 13 }}>{p.name}</div>
              <div style={{ fontSize: 11, color: '#64748b' }}>{p.bed} · {p.diag}</div>
              <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
                <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 999, background: (statusColor[p.status] || '#94a3b8') + '20', color: statusColor[p.status] || '#94a3b8', border: `1px solid ${(statusColor[p.status] || '#94a3b8')}40` }}>{p.status}</span>
                {p.ventilated && <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 999, background: '#eff6ff', color: '#2563eb', border: '1px solid #bfdbfe' }}>Vent</span>}
              </div>
            </div>
          ))}
        </div>

        <div>
          {selectedPatient && (
            <div className="card mb" style={{ borderLeft: '4px solid #0d6e6a', padding: '12px 16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                <div>
                  <div style={{ fontWeight: 900, fontSize: 18 }}>{selectedPatient.name} <span style={{ fontSize: 13, fontWeight: 400, color: '#64748b' }}>{selectedPatient.age}y</span></div>
                  <div style={{ fontSize: 13, color: '#475569' }}>{selectedPatient.diag} · {selectedPatient.bed} · {selectedPatient.doctor}</div>
                  <div style={{ fontSize: 12, color: '#94a3b8' }}>Admitted: {selectedPatient.since} · ID: {selectedPatient.id}</div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
                  <select
                    className="input"
                    style={{ width: 140, fontWeight: 700 }}
                    value={selectedPatient.status}
                    onChange={async (e) => {
                      const icuStatus = e.target.value;
                      try {
                        await ipApi.patchAdmission(selectedPatient.id, { icuStatus });
                        setSelectedPatient((p) => ({ ...p, status: icuStatus }));
                      } catch (err) {
                        window.alert(err.message || 'Could not update ICU status');
                      }
                    }}
                  >
                    {['Critical', 'Serious', 'Stable', 'Improving'].map((s) => <option key={s}>{s}</option>)}
                  </select>
                  <label style={{ fontSize: 12, display: 'flex', gap: 6, alignItems: 'center' }}>
                    <input
                      type="checkbox"
                      checked={!!selectedPatient.ventilated}
                      onChange={async (e) => {
                        const ventilated = e.target.checked;
                        try {
                          await ipApi.patchAdmission(selectedPatient.id, { ventilated });
                          setSelectedPatient((p) => ({ ...p, ventilated }));
                        } catch (err) {
                          window.alert(err.message || 'Could not update vent flag');
                        }
                      }}
                    />
                    Ventilated
                  </label>
                </div>
              </div>
            </div>
          )}

          <div className="period-tabs card mb" style={{ flexWrap: 'wrap' }}>
            {[{ id: 'vitals', label: '📊 Hourly Vitals' }, { id: 'ventilator', label: '🫁 Ventilator' }, { id: 'apache', label: '📋 APACHE II / GCS' }, { id: 'drips', label: '💉 Infusion Chart' }, { id: 'restraint', label: '🔒 Restraint / Sedation' }, { id: 'notes', label: '📝 ICU Notes' }].map(({ id, label }) => (
              <button key={id} type="button" className={`emr-chip ${tab === id ? 'active' : ''}`} onClick={() => setTab(id)}>{label}</button>
            ))}
          </div>

          {tab === 'vitals' && <HourlyVitalsTab patient={selectedPatient} />}
          {tab === 'ventilator' && <VentilatorPanel patient={selectedPatient} />}
          {tab === 'apache' && <ApacheTab patient={selectedPatient} />}
          {tab === 'drips' && <DripChartTab patient={selectedPatient} />}
          {tab === 'restraint' && <RestraintTab patient={selectedPatient} />}
          {tab === 'notes' && <IcuNotesTab patient={selectedPatient} notes={icuNotes} onSaveNote={createNote} />}
        </div>
      </div>
    </PageShell>
  );
}
